<template lang="html">
  <div>
    <!-- SAVED REPORT DIALOG -->
    <v-dialog
      v-model="showSavedReportDialog"
      :max-width="300"
    >
      <v-card>
        <v-card-title>
          <div class="headline">
            {{ savingReportId ? newSavedReportName === selectedSavedReportName ? 'Update' : 'Update/Rename' : 'New' }} Saved Report
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-text-field
            v-model="newSavedReportName"
            label="Name"
            type="text"
            autofocus
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="red--text darken-1"
            text
            @click="showSavedReportDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="blue--text darken-1"
            text
            :disabled="!newSavedReportName"
            @click="saveReport"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE SAVED REPORT DIALOG -->
    <v-dialog
      v-model="showDeleteSavedReportDialog"
      :max-width="300"
    >
      <v-card>
        <v-card-title>
          <div class="headline">
            Delete Saved Report
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          Are you sure you want to delete the saved report "{{ selectedSavedReportName }}"
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="blue--text darken-1"
            text
            @click="showDeleteSavedReportDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="red--text darken-1"
            text
            @click="deleteSavedReport"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EXPORT PDF DIALOG -->
    <v-dialog
      v-model="showExportPdfDialog"
      :max-width="300"
    >
      <v-card>
        <v-card-title>
          <div class="headline">
            Export to PDF
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-autocomplete
            v-model="selectedPaperSize"
            prepend-icon="mdi-move-resize"
            label="Size"
            :items="pdfPaperSizes"
            item-text="name"
            item-value="value"
          />
          <v-autocomplete
            v-model="selectedOrientation"
            prepend-icon="mdi-screen-rotation"
            label="Orientation"
            :items="pdfOrientations"
            item-text="name"
            item-value="value"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="red--text darken-1"
            text
            @click="showExportPdfDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="blue--text darken-1"
            :loading="isDataLoading"
            :disabled="isDataLoading"
            text
            @click="exportAsPdf"
          >
            Export
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EMAIL PDF DIALOG -->
    <v-dialog
      v-model="showEmailDialog"
      :max-width="300"
      persistent
    >
      <v-card>
        <v-card-title>
          <div class="headline">
            Send PDF in Email
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-text-field
            label="Emails"
            type="text"
            :value="value.rcpts"
            @change="v => value.rcpts = v && v.trim()"
          />
          <v-text-field
            label="Subject"
            type="text"
            :value="value.subject"
            @change="v => value.subject = v && v.trim()"
          />
          <v-text-field
            label="Message"
            type="text"
            :value="value.message"
            @change="v => value.message = v && v.trim()"
          />
          <v-autocomplete
            v-model="selectedPaperSize"
            prepend-icon="mdi-move-resize"
            label="Size"
            :items="pdfPaperSizes"
            item-text="name"
            item-value="value"
          />
          <v-autocomplete
            v-model="selectedOrientation"
            prepend-icon="mdi-screen-rotation"
            label="Orientation"
            :items="pdfOrientations"
            item-text="name"
            item-value="value"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="red--text darken-1"
            text
            @click="showEmailDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="blue--text darken-1"
            :loading="isDataLoading"
            :disabled="isDataLoading"
            text
            @click="saveAsPdf"
          >
            Preview
          </v-btn>
          <v-btn
            class="blue--text darken-1"
            :loading="isDataLoading"
            :disabled="isDataLoading"
            text
            @click="sendEmail"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SAVE REPORT CONFIG -->
    <v-card>
      <page-bar
        :title="title + ' Settings'"
        :loading="isDataLoading"
        :context-menu="reportBuilderContextMenu"
      />
      <api-errors :errors="errors" />

      <!--  DISPLAY REPORT FILTERS  -->
      <v-form
        ref="filterForm"
        v-model="formReady"
        :lazy-validation="true"
      >
        <v-layout wrap>
          <v-flex class="xs12 sm6 md4 px-2">
            <!-- SAVED REPORTS -->
            <v-autocomplete
              v-model="currentFilterId"
              prepend-icon="mdi-open-in-app"
              label="Saved Reports"
              :items="savedReports"
              item-text="name"
              item-value="id"
              autocomplete
              clearable
              @click:clear="$nextTick(() => currentFilterId = null)"
            />
          </v-flex>
        </v-layout>
        <v-layout
          v-if="filterSlots[0]"
          wrap
        >
          <!-- NAMED REPORT FILTERS -->
          <template
            v-for="(name, i) in filterSlots"
          >
            <v-flex
              :key="type + ':' + name + ':' + i"
              :class="filterClass(i)"
            >
              <slot
                :name="name"
                :filter="value"
              />
            </v-flex>
          </template>
        </v-layout>
        <slot :filter="value" />
      </v-form>

      <!-- BUTTONS -->
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          color="error"
          @click="resetFilters"
        >
          Reset
        </v-btn>
        <v-btn
          text
          :disabled="isDataLoading || showReport"
          color="primary"
          @click="generateReport"
        >
          Run
        </v-btn>
      </v-card-actions>
    </v-card>
    <!--  END REPORT FILTERS -->

    <!--  DISPLAY REPORT  -->
    <v-dialog
      v-model="showReport"
      fullscreen
      transition="dialog-bottom-transition"
      :overlay="false"
      scrollable
      class="ma-0 pa-0"
    >
      <v-card>
        <!-- REPORT TOOLBAR -->
        <page-bar
          fixed
          :on-close="() => {showReport = false}"
          :title="title"
          :hide-page-scrollbar="showReport"
          :context-menu="reportContextMenu"
        >
          <v-btn
            v-if="value.scale"
            icon
            :disabled="value.scale <= minScale"
            @click="shrinkReport"
          >
            <v-icon
              dark
              color="white"
            >
              mdi-magnify-minus-outline
            </v-icon>
          </v-btn>
          <v-btn
            v-if="value.scale"
            icon
            :disabled="value.scale >= maxScale"
            @click="enlargeReport"
          >
            <v-icon
              dark
              color="white"
            >
              mdi-magnify-plus-outline
            </v-icon>
          </v-btn>

          <v-btn
            icon
            @click="printReport"
          >
            <v-icon
              dark
              color="white"
            >
              mdi-printer
            </v-icon>
          </v-btn>
        </page-bar>
        <!-- REPORT VCARD -->
        <v-card-text
          id="reportVCard"
          class="px-2 py-0"
        >
          <report-table
            ref="reportDialog"
            :key="reportKey"
            v-model="showReport"
            :scale.sync="value.scale"
            :title="title"
            :report-data="reportData"
            :report-date-time="reportDateTime"
            :report-name="selectedSavedReportName"
            v-bind="$attrs"
          >
            <template
              v-for="(name) in cellSlots"
              :slot="name"
              slot-scope="slotData"
            >
              <slot
                :name="name"
                v-bind="slotData"
              />
            </template>
          </report-table>
        </v-card-text>
        <!-- empty v-card-actions helps data float to top -->
        <v-card-actions class="ma-0 pa-0" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import NavRoute from '../../mixins/navRoute'
import ErrorHandler from '../../mixins/errorHandler'
import rules from '../../mixins/rules'
import exporting from './mixins/exporting'
import ReportTable from './ReportTable'
// import moment from 'moment'
import {setItems, deepClone, displayTime, displayDate} from '../../util/helpers'

var smFilterCount = 0

export default {
  name: 'ReportBuilder',
  components: {ReportTable},
  mixins: [NavRoute, ErrorHandler, rules, exporting],
  props: {
    value: Object,
    title: String,
    type: String,
    command: String,
    savedReportCommand: String,
    defaults: Object,
    resources: {
      type: Function,
      default () {
        return []
      }
    },
    validateFilters: {
      type: Function,
      default (f) {
        return true
      }
    },
    reportFilters: {
      type: Function,
      default (f) {
        return null
      }
    },
    beforeRun: {
      type: Function,
      default (f) {return false}
    },
    reportFields: {
      type: Function,
      default (f) {
        return null
      }
    },
    reportOrder: {
      type: Function,
      default (f) {
        return null
      }
    },
    reportGrouping: {
      type: Function,
      default (f) {
        return null
      }
    },
    reportGroupingFields: {
      type: Function,
      default (f) {
        return null
      }
    },
    reportHaving: {
      type: Function,
      default (f) {
        return null
      }
    },
    afterLoad: {
      type: Function,
      default (r) {}
    },
    beforeLoad: {
      type: Function,
      default (r) {}
    },
    commandFilter: {
      type: Function,
      default (c) {return c}
    },
    minScale: {
      type: Number,
      default: 0.6
    },
    maxScale: {
      type: Number,
      default: 1
    },
    scaleIncrement: {
      type: Number,
      default: 0.10
    }
  },
  data: function () {
    return {
      formReady: false,
      showReport: false,
      source: null,
      reportData: [],
      savedReports: [],
      savedReportsJson: [],
      selectedReport: null,
      currentFilterId: null,
      showSavedReportDialog: false,
      showDeleteSavedReportDialog: false,
      showExportPdfDialog: false,
      showEmailDialog: false,
      saveAsMode: false,
      newSavedReportName: null,
      reportDateTime: null,
      isLoading: 0,
      reportKey: 0
    }
  },
  computed: {
    filterSlots () {
      return Object.keys(this.$scopedSlots).filter(sn => sn.startsWith('filter-'))
    },
    cellSlots () {
      return Object.keys(this.$scopedSlots).filter(sn => !sn.startsWith('filter-'))
    },
    reportContextMenu () {
      return [
        {
          title: 'Print Report',
          icon: 'mdi-printer',
          action: this.printReport
        },
        {}, // divider,
        {
          title: 'Export as PDF',
          icon: 'mdi-file-pdf',
          action: () => {this.openExportPdfDialog()}
        },
        {
          title: 'Export as EXCEL',
          icon: 'mdi-file-excel',
          action: this.outputXLS
        },
        {
          title: 'Export as CSV',
          icon: 'mdi-file-delimited',
          action: this.outputCSV
        },
        {}, // divider
        {
          title: 'Send in Email',
          icon: 'mdi-email',
          action: () => {this.openEmailDialog()}
        },
        {}, // divider
        {
          title: (this.currentFilterId ? 'Update Saved' : 'Save') + ' Report Settings',
          icon: 'mdi-content-save',
          action: this.showSaveDialog
        },
        {
          title: 'Save Report Settings as ...',
          icon: 'mdi-content-save-settings',
          hide: !this.selectedSavedReportName,
          action: this.showSaveAsDialog
        }
      ]
    },
    reportBuilderContextMenu () {
      return [
        {
          title: 'Delete Saved Report',
          icon: 'mdi-delete-forever',
          action: () => {this.openDeleteSavedReportDialog()},
          disabled: !this.currentFilterId
        }
      ]
    },
    isDataLoading () {
      return this.isLoading > 0
    },
    savedReportsMap () {
      return this.savedReports.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
    },
    selectedSavedReport () {
      return this.savedReportsMap[this.currentFilterId] || {}
    },
    selectedSavedReportName () {
      return this.selectedSavedReport.name || null
    },
    selectedSavedReportFilters () {
      return JSON.parse(this.selectedSavedReport.filter) || deepClone(this.defaults)
    },
    savingReportId () {
      return this.saveAsMode ? null : this.currentFilterId
    }
  },
  watch: {
    selectedSavedReport () {
      if (!this.selectedSavedReportName) return
      setItems(this.value, this.selectedSavedReportFilters, this.defaults)
    },
    showSavedReportDialog (val) {
      if (!val) return
      if (!this.saveAsMode && this.selectedSavedReportName) {
        this.newSavedReportName = this.selectedSavedReportName
      } else {
        this.newSavedReportName = ''
      }
    }
  },
  mounted () {
    console.log('Mounted Report', this)
    console.log('Mounted Report2', this.$slots['filter-type'])
    console.log('Mounted Report3', this.$slots['summary-body'])
    this.loadReportFilterResources()
  },
  methods: {
    filterClass (index) {
      if (index < 1) smFilterCount = 0
      const isLarge = index < 0 ? false : this.isLargeFilter(index)
      const nextIsLarge = this.isLargeFilter(index + 1)
      var result = 'xs12 sm6 md4'
      if (isLarge || nextIsLarge && smFilterCount < 1) {
        smFilterCount = 0
        result = 'xs12 sm12 md12'
      } else if (nextIsLarge && smFilterCount > 2) {
        smFilterCount = 0
        result = 'xs12 sm6 md12'
      } else if (nextIsLarge && smFilterCount === 1) {
        smFilterCount = 0
        result = 'xs12 sm6 md8'
      } else {
        smFilterCount ++
        if (smFilterCount > 3) {
          smFilterCount = 1
        }
      }
      result += ' ft-report-filter-wrap'
      return result += ' px-2 ' + (this.filterSlots[index] || '') + ' mdfc_' + smFilterCount + ' nil_' + nextIsLarge + ' il_' + isLarge
    },
    isLargeFilter (index) {
      return (this.filterSlots[index] || '').endsWith('-lg')
    },
    displayTime,
    displayDate,
    openDeleteSavedReportDialog () {
      this.showDeleteSavedReportDialog = true
    },
    openEmailDialog () {
      this.showEmailDialog = true
    },
    openExportPdfDialog () {
      this.showExportPdfDialog = true
    },
    resetFilters () {
      setItems(this.value, this.defaults)
      this.currentFilterId = null
      this.showReport = false
    },
    isFormValid () {
      console.log('validating form', this.$refs.filterForm)
      if (this.$refs.filterForm) {
        return this.$refs.filterForm.validate()
      }
      return this.formReady
    },
    showSaveAsDialog () {
      this.saveAsMode = true
      this.showSavedReportDialog = true
    },
    showSaveDialog () {
      this.saveAsMode = false
      this.showSavedReportDialog = true
    },
    saveReport () {
      const id = this.savingReportId
      if (!this.newSavedReportName) return
      this.$ftAPI().put(this.addResourceErrorHandler(
        {
          command: this.savedReportCommand,
          afterLoad: (r) => {
            this.loadSavedReports(r && r[0] ? r[0].id : null)
          }
        }), {'id': id, 'type': this.type, 'name': this.newSavedReportName, 'filter': JSON.stringify(this.value)}
      )
      this.showSavedReportDialog = false
    },
    deleteSavedReport () {
      if (!this.selectedSavedReport) return
      this.$ftAPI().delete(this.addResourceErrorHandler(
        {
          command: this.savedReportCommand,
          afterLoad: () => {
            this.currentFilterId = null
            this.loadSavedReports()
          }
        }), this.selectedSavedReport
      )
      this.showDeleteSavedReportDialog = false
    },
    reportCommand () {
      const params = {
        filter: this.reportFilters(this.value),
        include_fields: this.reportFields(this.value)
      }
      const orderFields = this.reportOrder(this.value)
      if (orderFields && orderFields.length) {
        params.order_by = orderFields
      }
      const groupFields = this.reportGrouping(this.value)
      if (groupFields && groupFields.length) {
        params.group_by = groupFields
      }
      const havingFilters = this.reportHaving(this.value)
      if (havingFilters && havingFilters.length) {
        params.having = havingFilters
      }
      return this.commandFilter({
        command: this.command,
        load: this.reportData,
        loading: this.setIsLoading,
        afterLoad () {
          this.reportDateTime = this.$ftAPI().clubMoment()
          this.reportKey ++ // Increment report key so the report table is forced to be re-rendered
          this.showReport = true
        },
        params: params
      })
    },
    setIsLoading (val) {
      if (this.isLoading !== undefined) {
        this.isLoading += val ? 1 : -1
      }
    },
    generateReport () {
      if (this.beforeRun() || !this.validateFilters()) return
      this.$ftAPI().get(this.addResourceErrorHandler(
        [this.reportCommand()]
      ))
    },
    loadReportFilterResources () {
      const resources = this.resources() || []
      for (let r of resources) {
        r.loading = this.setIsLoading
      }
      resources.push(this.savedReportListCommand())
      console.log('Loading resources', resources)
      this.$ftAPI().get(this.addResourceErrorHandler(resources))
    },
    savedReportListCommand (selectId) {
      return {
        command: this.savedReportCommand,
        load: this.savedReports,
        afterLoad: () => {
          if (selectId) this.currentFilterId = selectId
        },
        loading: this.setIsLoading,
        params: {
          filter: [{field: 'type', operator: 'EQUAL', values: [this.type]}]
        }
      }
    },
    loadSavedReports (selectId) {
      // Re-load location tables
      this.$ftAPI().get(this.addResourceErrorHandler(this.savedReportListCommand(selectId)))
    },
    enlargeReport () {
      this.value.scale += this.scaleIncrement
      this.value.scale = this.value.scale > this.maxScale ? this.maxScale : this.value.scale
      this.$emit('update:scale', this.value.scale)
    },
    shrinkReport () {
      this.value.scale -= this.scaleIncrement
      this.value.scale = this.value.scale < this.minScale ? this.minScale : this.value.scale
      this.$emit('update:scale', this.value.scale)
    }
  }
}
</script>
<style>
  .ft-report-filter-wrap:empty {
    display: none !important;
  }
</style>
